/**
 * Live brand configuration.
 *
 * A tiny mutable store, seeded from the bundled `BRAND` constant and replaced
 * once the API responds.
 *
 * It exists because not every consumer is a React component. `generateWhatsAppUrl`
 * in lib/whatsapp.js is a plain function called from dozens of places, and it
 * needs the current WhatsApp number. Threading settings through every call site
 * would mean touching every button on the site; reading from here at call time
 * means they all pick up an edit automatically.
 *
 * Components should still use `useSiteSettings()` so they re-render when values
 * change. This store is for code that runs outside React.
 */
import { BRAND } from "./brand";

let current = { ...BRAND };

/** Current brand values. Never returns null — falls back to the bundled copy. */
export function getBrand() {
  return current;
}

/**
 * Replaces the live values.
 *
 * Merged over the bundled defaults rather than replacing them, so a field the
 * administrator has left blank keeps its original value instead of rendering
 * as an empty string.
 */
export function setBrand(next) {
  if (!next) return;
  current = { ...BRAND, ...next, stats: { ...BRAND.stats, ...(next.stats ?? {}) } };
}

/**
 * Splits a display figure such as "5,000+" into an animatable number and its
 * suffix, so the counter component can still count up to an edited value.
 */
export function splitStat(value, fallbackSuffix = "") {
  if (typeof value === "number") return { value, suffix: fallbackSuffix };

  const text = String(value ?? "").trim();
  const digits = text.replace(/[^\d.]/g, "");
  const parsed = Number.parseFloat(digits);

  if (!Number.isFinite(parsed)) return { value: 0, suffix: fallbackSuffix };

  // Everything after the numeric part is the suffix: "5,000+" -> "+".
  const suffix = text.slice(text.search(/[\d.]/)).replace(/^[\d.,\s]+/, "") || fallbackSuffix;

  return { value: parsed, suffix };
}
